import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useMatches } from '@tanstack/react-router';
import { useHotkeys } from 'react-hotkeys-hook';
import { TagsV2 } from '@frontend/api-tag';
import { VoiceMailboxApi } from '@frontend/api-voicemail-boxes';
import { VoicemailTypes } from '@frontend/api-voicemails';
import { NoAccessEmptyState as NoAccessEmptyStateSvg } from '@frontend/empty-states#svg';
import { useTranslation } from '@frontend/i18n';
import { GlobalPanelEntry } from '@frontend/panel-engine';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useQueueStore } from '@frontend/queue';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Accordion, Button, Heading, Text, useAlert, useModalControl } from '@frontend/design-system';
import { useVoicemailBoxList } from '../../hooks/use-voicemail-box';
import {
  useVoicemailTagMutation,
  optimisticAddVoicemailTag,
  optimisticRemoveVoicemailTag,
} from '../../hooks/use-voicemail-tag-mutations';
import { transcriptionTrackingId } from '../../pendo-tracking';
import { queryKeys } from '../../query-keys';
import { useVoicemailDetailsShallowStore } from '../../stores/voicemail-details-store';
import { VoicemailTableDataRow } from '../all-calls/types';
import { AddTag } from '../voicemails/voicemail-tag-selector';
import { Forwardvoicemail } from '../voicemails/voicemails-forward';
import { ForwardVoicemailDynamicData, ForwardVoicemailModalData } from '../voicemails/voicemails-multi.component';
import { VoicemailDetailsAccordionType } from './types';
import { getTranscriptionUniqueKey } from './utils';
import {
  CallDetailsSectionBody,
  ContactActions,
  ContactSection,
  TagsSectionBody,
  TranscriptSectionBody,
  VoicemailMedia,
} from './voicemail-details-panel-sections';

export const defaultForwardVoicemailModalData = {};

const VoicemailDetailsScreenV2 = () => {
  const { t } = useTranslation('calls');
  const panelRef = useRef<HTMLDivElement>(null);
  const alerts = useAlert();
  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();

  const match = useMatches();

  // Use the transcription context store instead of slide panel context
  const {
    rows,
    voicemailData,
    currentRowId,
    sectionFocus,
    userHasAccess,
    getNextPage,
    setSectionFocus,
    setRows,
    setVoicemailData,
    setGetNextPage,
    toggleRowSelected,
    deleteVoicemail,
    updateReadStatus,
    navigateToNext,
    navigateToPrevious,
    voicemailsQueryKey,
    isDetached,
    setIsDetached,
  } = useVoicemailDetailsShallowStore(
    'rows',
    'voicemailData',
    'currentRowId',
    'sectionFocus',
    'userHasAccess',
    'getNextPage',
    'setSectionFocus',
    'setRows',
    'setVoicemailData',
    'setGetNextPage',
    'toggleRowSelected',
    'deleteVoicemail',
    'updateReadStatus',
    'navigateToNext',
    'navigateToPrevious',
    'voicemailsQueryKey',
    'isDetached',
    'setIsDetached'
  );

  useEffect(() => {
    // Once we leave the voicemails page, remove navigation
    if (match.every((m) => m.id !== '/calls/voicemails')) {
      setIsDetached(true);
    }
  }, [match, setIsDetached]);

  const row = voicemailData;

  // Undocumented feature to copy voicemail shortlink, paste in topbar (not search) to open up voicemail details
  useHotkeys('ctrl+., cmd+.', () => {
    try {
      const voicemailId = voicemailData?.voicemailId;
      const locationId = voicemailData?.locationIds[0];
      const copyString = `${
        window.location.origin + window.location.pathname
      }?linktype=voicemail&voicemailId=${voicemailId}&locationId=${locationId}`;
      navigator.clipboard.writeText(copyString);
      alerts.success(t('Shortlink copied successfully!'));
    } catch {
      alerts.warning(t('Failed to copy shortlink.'));
    }
  });

  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');

  const { data: voicemailBoxList } = useVoicemailBoxList({
    phoneConfig,
    locationIds: selectedLocationIds,
  });

  const { data: transcription, status: transcriptionStatus } = useScopedQuery({
    queryKey: queryKeys.voicemailTranscript(row?.channelId ?? ''),
    queryFn: async () => {
      const response = await VoiceMailboxApi.getTranscription(row?.channelId ?? '');
      return response;
    },
    enabled: !!row,
  });

  const currentRowIndex = rows.findIndex((r) => {
    const transcriptionUniqueKey = getTranscriptionUniqueKey(r);
    return transcriptionUniqueKey === currentRowId;
  });

  const transcriptionUniqueKey = getTranscriptionUniqueKey(row);

  const [openedAccordionId, setOpenedAccordionId] = useState<VoicemailDetailsAccordionType>(
    sectionFocus || 'transcript'
  );

  useLayoutEffect(() => {
    if (!!sectionFocus && openedAccordionId !== sectionFocus) {
      setOpenedAccordionId(sectionFocus);
    }
  }, [openedAccordionId, sectionFocus]);

  // Tags data and mutations
  const { data: tagData } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      enabled: row?.tags?.length === 0,
    },
  });

  const tagsMap =
    tagData?.tags.reduce((acc, tag) => {
      acc[tag.id] = tag;
      return acc;
    }, {} as Record<string, (typeof tagData)['tags'][number]>) ?? {};

  const assignedTags = !!row?.tags?.length ? row.tags : row?.tagIds?.map((tagId: string) => tagsMap[tagId]) || [];

  /**
   * This effect is used to fetch the next page of voicemails when the user reaches the end of the current page of results.
   */
  useEffect(() => {
    if (currentRowIndex + 1 >= rows.length) {
      getNextPage?.().then((res) => {
        setRows(res.data?.pages.flatMap((page) => page.rows || []) ?? []);
        setGetNextPage(res.data?.pages.at(-1)?.meta.hasNext ? getNextPage : undefined);
      });
    }
  }, [currentRowIndex, rows.length, getNextPage, setRows, setGetNextPage]);

  const { addTag, removeTag } = useVoicemailTagMutation({
    queryKey: voicemailsQueryKey ?? [],
    voicemailId: row?.voicemailId ?? '',
    optimisticRemoveTag: (args) => {
      if (!voicemailData) return;

      const updatedVoicemailData = {
        ...voicemailData,
        tags: voicemailData.tags.filter((t) => t.id !== args.tag.id),
        tagIds: voicemailData.tagIds.filter((id) => id !== args.tag.id),
      } satisfies VoicemailTableDataRow;
      setVoicemailData(updatedVoicemailData);

      if (rows.length) {
        // List mode with multiple rows
        const newData = optimisticRemoveVoicemailTag({
          oldData: args.oldData,
          tag: args.tag,
          voicemailId: row?.voicemailId ?? '',
        });

        if (newData) {
          setRows(newData.pages.flatMap((page) => page.rows || []));
        }

        return newData;
      }

      return updatedVoicemailData;
    },
    optimisticAddTag: (args) => {
      if (!voicemailData) return;

      const updatedVoicemailData = {
        ...voicemailData,
        tags: [...voicemailData.tags, args.tag],
        tagIds: [...voicemailData.tagIds, args.tag.id],
      } satisfies VoicemailTableDataRow;
      setVoicemailData(updatedVoicemailData);

      if (rows.length) {
        // List mode with multiple rows
        const newData = optimisticAddVoicemailTag({
          oldData: args.oldData,
          tag: args.tag,
          voicemailId: row?.voicemailId ?? '',
        });

        if (newData) {
          setRows(newData.pages.flatMap((page) => page.rows || []));
        }

        return newData;
      }

      return updatedVoicemailData;
    },
    onSuccess: ({ op }) => {
      if (op === 'add') {
        alerts.success(t('Tag added successfully.'));
      }
      if (op === 'remove') {
        alerts.success(t('Successfully removed tag.'));
      }
    },
    onFailure: ({ op, oldData }) => {
      if (op === 'add') alerts.error(t('Failed to add tag.'));
      if (op === 'remove') alerts.error(t('Failed to remove tag.'));

      if (oldData) {
        if ('pages' in oldData) {
          setRows(oldData.pages.flatMap((page) => page.rows || []));
          setVoicemailData(
            oldData.pages.flatMap((page) => page.rows || []).find((r) => r.voicemailId === row?.voicemailId)
          );
        } else {
          setVoicemailData(oldData);
        }
      }
    },
  });

  const [forwardVoicemailModalData, setForwardVoicemailModalData] = useState<ForwardVoicemailModalData>(
    defaultForwardVoicemailModalData as ForwardVoicemailModalData
  );
  const doubleActionControl = useModalControl();

  const openForwardVoicemailModal = (data: ForwardVoicemailDynamicData) => {
    setForwardVoicemailModalData({
      ...data,
      voicemailBoxList: voicemailBoxList || [],
      onDelete: deleteVoicemail ?? (() => console.log('No delete function defined')),
    });
    doubleActionControl.openModal();
  };

  const handleUpdateReadStatus = ({ markIsRead, voicemailId }: { markIsRead: boolean; voicemailId: string }) => {
    updateReadStatus?.({
      index: currentRowIndex,
      status: markIsRead ? VoicemailTypes.ViewStatus.VIEW_STATUS_READ : VoicemailTypes.ViewStatus.VIEW_STATUS_UNREAD,
      voicemailId: voicemailId,
    });
  };

  const { enqueue } = useQueueStore.getState();

  return (
    <div
      ref={panelRef}
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        height: '100%',
        padding: theme.spacing(3),
      }}
    >
      <Forwardvoicemail modalProps={doubleActionControl.modalProps} forwardData={forwardVoicemailModalData} />

      <header
        style={{
          display: 'grid',
          alignItems: 'center',
          padding: theme.spacing(1),
          gridTemplateColumns: '1fr max-content',
        }}
      >
        <Heading level={2} css={{ margin: 0 }}>
          {t('Voicemail Details')}
        </Heading>
        <Button
          css={{
            justifySelf: 'end',
            width: 'max-content',
          }}
          variant='secondary'
          label={t('Close panel')}
          iconName='x'
          size='large'
          onClick={() => {
            enqueue('voicemail-details', {
              payload: {
                type: 'close',
              },
            });
          }}
        />
      </header>

      {row && (
        <div style={{ overflow: 'auto' }}>
          <ContactSection
            contact={{
              id: row.contactId,
              firstName: row.firstName,
              lastName: row.lastName,
              profileName: isNaN(Number(row.contactName)) ? row.contactName : t('Unknown'),
              defaultName: t('Unknown'),
              number: row.contactNumber,
            }}
            locationId={row.locationIds?.[0] ?? ''}
            isMarked={!row.readAt}
          >
            <ContactActions
              contact={{ name: row.contactName, id: row.contactId, number: row.contactNumber }}
              locationId={row.locationIds?.[0] ?? ''}
              mediaFilePath={row.mediaFilePath}
              date={row.dateTime}
              message={row}
              updateReadStatus={(markIsRead) => handleUpdateReadStatus({ markIsRead, voicemailId: row.voicemailId })}
              openForwardVoicemailModal={() => {
                openForwardVoicemailModal({
                  currentVoicemailBoxId: row.mailboxId,
                  mediaId: row.mediaId,
                  mediaPath: row.mediaFilePath,
                  currentVoicemailBoxName: row.voiceMailBoxName,
                  vmId: row.voicemailId,
                  rowIndex: currentRowIndex,
                });
              }}
              isVmBoxRestricted={!userHasAccess}
            />
          </ContactSection>

          {!!userHasAccess && (
            <>
              <VoicemailMedia
                message={row}
                mediaFilePath={row.mediaFilePath}
                updateReadStatus={() => handleUpdateReadStatus({ markIsRead: true, voicemailId: row.channelId })}
              />

              <Accordion
                variant='blank'
                controlledValue={openedAccordionId}
                onChange={(value) => {
                  setOpenedAccordionId(value as VoicemailDetailsAccordionType);
                  setSectionFocus(value as VoicemailDetailsAccordionType);
                }}
                padding={0}
                css={css`
                  padding: ${theme.spacing(1)};
                  > div:not(:last-child) {
                    margin-bottom: ${theme.spacing(2)};
                  }
                `}
              >
                <Accordion.Item title={t('Transcript')} value='transcript' css={styles.accordionItemStyle}>
                  <Accordion.Header
                    title={t('Transcript')}
                    css={styles.accordionHeaderStyle(openedAccordionId, 'transcript')}
                  />
                  <Accordion.Body>
                    <TranscriptSectionBody
                      status={transcriptionStatus}
                      message={row}
                      transcription={transcription}
                      updateReadStatus={() =>
                        handleUpdateReadStatus({ markIsRead: true, voicemailId: row.voicemailId })
                      }
                      isOpen={openedAccordionId === 'transcript'}
                    />
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item title={t('About this voicemail')} value='details' css={styles.accordionItemStyle}>
                  <Accordion.Header
                    title={t('About this voicemail')}
                    css={styles.accordionHeaderStyle(openedAccordionId, 'details')}
                  />
                  <Accordion.Body>
                    <CallDetailsSectionBody
                      mailboxName={row.voiceMailBoxName}
                      date={row.dateTime}
                      locationId={row.locationIds?.[0]}
                    />
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item title={t('Tags')} value='tags' css={styles.accordionItemStyle}>
                  <Accordion.Header css={styles.accordionHeaderStyle(openedAccordionId, 'tags')}>
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-between;
                        width: 100%;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: ${theme.spacing(0.5)};
                        `}
                      >
                        <Text
                          css={css`
                            font-size: ${theme.fontSize(18)};
                          `}
                          weight='bold'
                        >
                          {t('Tags')}
                        </Text>
                        <Text size='small' color='subdued'>
                          {assignedTags.length > 0 ? assignedTags.length : ''}
                        </Text>
                      </div>
                      <AddTag existingTagIds={assignedTags.map((tag) => tag.id)} onApplyTag={addTag} />
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <TagsSectionBody tags={assignedTags} onApplyTag={addTag} onRemoveTag={removeTag} />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </>
          )}

          {!userHasAccess && (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: `${theme.spacing(4)}`,
              }}
            >
              <NoAccessEmptyStateSvg css={{ width: 375, height: 375 }} />
              <Text
                textAlign='center'
                css={{ color: theme.colors.neutral50, padding: theme.spacing(0, 1.5), width: theme.spacing(42) }}
              >
                {!!row?.voiceMailBoxName
                  ? t(
                      `This voicemail is in a restricted voicemail box, {{vmBoxName}}. Contact your admin if you need access.`,
                      { vmBoxName: row?.voiceMailBoxName }
                    )
                  : t('This voicemail is in a restricted voicemail box. Contact your admin if you need access.')}
              </Text>
            </div>
          )}
        </div>
      )}

      {!!userHasAccess && (
        <div
          style={{
            display: 'grid',
            padding: theme.spacing(2, 1, 1),
            justifyContent: 'end',
            gridTemplateColumns:
              '[first-start] max-content [first-end] ' +
              '1fr' + // gap
              '[second-start] max-content [second-end] ' +
              `${theme.spacing(1)} ` + // gap
              '[third-start] max-content [third-end]',
          }}
        >
          <Button
            trackingId={transcriptionTrackingId({ subComponent: 'panel', context: 'delete' })}
            size='large'
            destructive
            variant='tertiary'
            iconName='trash'
            css={{ gridColumn: 'first-start / first-end' }}
            onClick={() => {
              if (!deleteVoicemail || !row) return;
              deleteVoicemail({
                index: currentRowIndex,
                voicemailId: row?.voicemailId,
                mailboxId: row?.mailboxId,
              });
              if (currentRowIndex + 1 >= rows.length) {
                if (toggleRowSelected && transcriptionUniqueKey) {
                  toggleRowSelected(transcriptionUniqueKey, false, currentRowIndex);
                }
                enqueue('voicemail-details', { payload: { type: 'close' } });
                return;
              }
              // Navigate to next voicemail after deletion
              navigateToNext();
            }}
          >
            {t('Delete Voicemail')}
          </Button>
          {!isDetached && (
            <>
              <Button
                variant='secondary'
                trackingId={transcriptionTrackingId({ subComponent: 'panel', context: 'previous' })}
                size='large'
                css={{ width: 'auto', marginLeft: 'auto', gridColumn: 'second-start / second-end' }}
                disabled={currentRowIndex - 1 < 0}
                onClick={() => {
                  navigateToPrevious();
                }}
              >
                {t('Previous')}
              </Button>

              <Button
                variant='secondary'
                trackingId={transcriptionTrackingId({ subComponent: 'panel', context: 'next' })}
                size='large'
                css={{ width: 'auto', gridColumn: 'third-start / third-end' }}
                disabled={currentRowIndex + 1 >= rows.length}
                onClick={() => {
                  navigateToNext();
                }}
              >
                {t('Next')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const VoicemailDetailsGlobalPanel = () => {
  const { enqueue } = useQueueStore();

  return (
    <GlobalPanelEntry
      id='voicemail-details'
      width='medium'
      onClose={() => enqueue('voicemail-details', { payload: { type: 'close' } })}
    >
      <VoicemailDetailsScreenV2 />
    </GlobalPanelEntry>
  );
};

const styles = {
  accordionHeaderStyle: (
    openedAccordionId: VoicemailDetailsAccordionType,
    currentAccordionType: VoicemailDetailsAccordionType
  ) => css`
    background-color: ${openedAccordionId === currentAccordionType ? theme.colors.neutral5 : 'transparent'};
    border-bottom: none;
    padding: ${theme.spacing(2, 0, 2, 2)};
  `,

  accordionItemStyle: css`
    border-radius: ${theme.spacing(1)};
    border: 1px solid ${theme.colors.neutral20};
    :last-child {
      border-bottom: 1px solid ${theme.colors.neutral20};
    }
  `,
};
